import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import aiAssistant from './aiAssistant';

// Get available exams for user's level
export const getAvailableExams = async (levelId, subjectId = null) => {
  try {
    console.log('🔍 Fetching exams with:', { levelId, subjectId });
    
    const examsRef = collection(db, 'exams');
    let q;
    
    if (levelId === null) {
      // For super admins - get all exams across all levels
      console.log('👑 Super admin mode: fetching all exams');
      q = query(
        examsRef,
        where('isActive', '==', true)
      );
    } else {
      // For regular users - filter by level
      console.log('👤 Regular user mode: filtering by level:', levelId);
      q = query(
        examsRef,
        where('levelId', '==', levelId),
        where('isActive', '==', true)
      );
    }
    
    if (subjectId) {
      console.log('📚 Filtering by subject:', subjectId);
      q = query(q, where('subjectId', '==', subjectId));
    }
    
    const querySnapshot = await getDocs(q);
    const exams = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by createdAt in JavaScript to avoid composite index
    const sortedExams = exams.sort((a, b) => {
      const dateA = a.createdAt?.toMillis ? a.createdAt.toMillis() : new Date(a.createdAt).getTime();
      const dateB = b.createdAt?.toMillis ? b.createdAt.toMillis() : new Date(b.createdAt).getTime();
      return dateB - dateA; // descending order
    });
    
    console.log('✅ Exams fetched successfully:', {
      total: sortedExams.length,
      exams: sortedExams.map(e => ({
        id: e.id,
        title: e.title,
        subjectId: e.subjectId,
        levelId: e.levelId,
        isActive: e.isActive
      }))
    });
    
    return sortedExams;
  } catch (error) {
    console.error('❌ Error fetching exams:', error);
    throw error;
  }
};

// Get single exam by ID
export const getExam = async (examId) => {
  try {
    const docRef = doc(db, 'exams', examId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } else {
      throw new Error('Exam not found');
    }
  } catch (error) {
    console.error('Error fetching exam:', error);
    throw error;
  }
};

// Submit exam results
export const submitExamResult = async (resultData) => {
  try {
    console.log('💾 Submitting exam result to Firestore:', resultData);
    
    const result = {
      userId: resultData.userId,
      examId: resultData.examId,
      levelId: resultData.levelId,
      subjectId: resultData.subjectId,
      score: resultData.score,
      totalMarks: resultData.totalMarks,
      percentage: resultData.percentage,
      passed: resultData.passed,
      timeTaken: resultData.timeTaken,
      answers: resultData.answers,
      startedAt: resultData.startedAt,
      completedAt: serverTimestamp(),
      createdAt: serverTimestamp()
    };

    console.log('📝 Document to be saved:', result);
    
    const docRef = await addDoc(collection(db, 'exam_results'), result);
    
    const savedResult = {
      id: docRef.id,
      ...result
    };
    
    console.log('✅ Exam result saved successfully:', savedResult);
    return savedResult;
  } catch (error) {
    console.error('❌ Error submitting exam result:', error);
    throw error;
  }
};

// Get user's exam results
export const getUserExamResults = async (userId, levelId, limit = 10) => {
  try {
    const resultsRef = collection(db, 'exam_results');
    let q = query(
      resultsRef,
      where('userId', '==', userId),
      orderBy('completedAt', 'desc'),
      limit(limit)
    );
    
    const querySnapshot = await getDocs(q);
    let results = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter by levelId in JavaScript to avoid composite index
    results = results.filter(result => result.levelId === levelId);
    
    return results;
  } catch (error) {
    console.error('Error fetching exam results:', error);
    throw error;
  }
};

// Get exam results for a specific exam
export const getExamResults = async (examId) => {
  try {
    const resultsRef = collection(db, 'exam_results');
    const q = query(
      resultsRef,
      where('examId', '==', examId),
      orderBy('completedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching exam results:', error);
    throw error;
  }
};

// Calculate exam score
export const calculateScore = async (userAnswers, exam) => {
  console.log('🧮 Calculating score:', { userAnswers, exam });
  
  // Debug: Log the entire exam structure
  console.log('📋 Exam questions structure:', exam.questions.map((q, i) => ({
    index: i,
    type: q.type,
    correctAnswer: q.correctAnswer,
    correctAnswerType: typeof q.correctAnswer,
    question: q.question?.substring(0, 50) + '...'
  })));
  
  // Debug: Log user answers structure
  console.log('👤 User answers structure:', userAnswers.map((a, i) => ({
    index: i,
    answer: a?.answer,
    answerType: typeof a?.answer,
    hasAnswer: a !== null
  })));
  
  let totalScore = 0;
  let correctAnswers = 0;
  
  const processedAnswers = await Promise.all(userAnswers.map(async (userAnswer, index) => {
    const question = exam.questions[index];
    if (!question) {
      console.warn('⚠️ Question not found at index:', index);
      return null;
    }
    
    let isCorrect = false;
    let marks = 0;
    
    console.log('📝 Processing question:', { index, question, userAnswer });
    
    if (question.type === 'multiple_choice') {
      console.log('🔍 Multiple choice comparison:', {
        userAnswer: userAnswer.answer,
        correctAnswer: question.correctAnswer,
        userAnswerType: typeof userAnswer.answer,
        correctAnswerType: typeof question.correctAnswer,
        areEqual: userAnswer.answer === question.correctAnswer
      });
      isCorrect = userAnswer.answer === question.correctAnswer;
      marks = isCorrect ? question.marks : 0;
    } else if (question.type === 'true_false') {
      console.log('🔍 True/False comparison:', {
        userAnswer: userAnswer.answer,
        correctAnswer: question.correctAnswer,
        userAnswerType: typeof userAnswer.answer,
        correctAnswerType: typeof question.correctAnswer,
        areEqual: userAnswer.answer === question.correctAnswer,
        convertedComparison: `${userAnswer.answer} === ${question.correctAnswer === 1} = ${userAnswer.answer === (question.correctAnswer === 1)}`
      });
      // Convert numeric correctAnswer (0/1) to boolean (false/true) for comparison
      const correctBoolean = question.correctAnswer === 1;
      isCorrect = userAnswer.answer === correctBoolean;
      marks = isCorrect ? question.marks : 0;
    } else if (question.type === 'short_answer') {
      // Use AI evaluation for short answers
      console.log('🤖 Using AI to evaluate short answer...');
      
      try {
        const evaluation = await aiAssistant.evaluateShortAnswer(
          userAnswer.answer,
          question.correctAnswer,
          question,
          'grade7' // You might want to pass the actual user level
        );
        
        console.log('🧠 AI evaluation result:', evaluation);
        
        isCorrect = evaluation.isCorrect;
        marks = evaluation.score >= 0.7 ? question.marks : 0; // Full marks or no marks
        
        console.log('📊 AI-based scoring:', { isCorrect, marks, evaluation });
      } catch (error) {
        console.error('🤖 AI evaluation failed, falling back to exact match:', error);
        
        // Fallback to exact matching
        isCorrect = userAnswer.answer.toLowerCase().trim() === question.correctAnswer.toLowerCase().trim();
        marks = isCorrect ? question.marks : 0;
      }
    }
    
    if (isCorrect) correctAnswers++;
    totalScore += marks;
    
    console.log('✅ Question result:', { 
      index, 
      isCorrect, 
      marks, 
      totalScore,
      userAnswer: userAnswer?.answer,
      correctAnswer: question.correctAnswer,
      comparison: `${userAnswer?.answer} === ${question.correctAnswer} = ${userAnswer?.answer === question.correctAnswer}`
    });
    
    return {
      questionId: question.id || index,
      answer: userAnswer.answer,
      isCorrect,
      marks,
      timeSpent: userAnswer.timeSpent || 0
    };
  }));
  
  const validAnswers = processedAnswers.filter(Boolean);
  
  const percentage = exam.totalMarks > 0 ? Math.round((totalScore / exam.totalMarks) * 100) : 0;
  const passed = percentage >= exam.passingMarks;
  
  console.log('📊 Final score calculation:', {
    totalScore,
    totalMarks: exam.totalMarks,
    percentage,
    passed,
    correctAnswers,
    totalQuestions: exam.questions.length
  });
  
  return {
    score: totalScore,
    totalMarks: exam.totalMarks,
    percentage,
    passed,
    correctAnswers,
    totalQuestions: exam.questions.length,
    answers: validAnswers
  };
};

// Get exam statistics for admin
export const getExamResult = async (resultId) => {
  try {
    const resultDoc = await getDoc(doc(db, 'exam_results', resultId));
    if (resultDoc.exists()) {
      return {
        id: resultDoc.id,
        ...resultDoc.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching exam result:', error);
    throw error;
  }
};

export const getExamStats = async (examId) => {
  try {
    const results = await getExamResults(examId);
    
    if (results.length === 0) {
      return {
        totalAttempts: 0,
        averageScore: 0,
        passRate: 0,
        highestScore: 0,
        lowestScore: 0
      };
    }

    const scores = results.map(result => result.percentage);
    const passingScores = scores.filter(score => score >= 50);

    const stats = {
      totalAttempts: results.length,
      averageScore: Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length),
      passRate: Math.round((passingScores.length / results.length) * 100),
      highestScore: Math.max(...scores),
      lowestScore: Math.min(...scores)
    };

    return stats;
  } catch (error) {
    console.error('Error fetching exam stats:', error);
    throw error;
  }
};

// Check if user can retake exam
export const canRetakeExam = async (userId, examId, maxAttempts = 3) => {
  try {
    const resultsRef = collection(db, 'exam_results');
    const q = query(
      resultsRef,
      where('userId', '==', userId),
      where('examId', '==', examId)
    );
    
    const querySnapshot = await getDocs(q);
    const attempts = querySnapshot.size;
    
    return attempts < maxAttempts;
  } catch (error) {
    console.error('Error checking retake eligibility:', error);
    return false;
  }
};

const examServices = {
  getAvailableExams,
  getExam,
  submitExamResult,
  getUserExamResults,
  getExamResults,
  calculateScore,
  getExamStats,
  canRetakeExam
};

export default examServices;
